import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink, FiSave } from 'react-icons/fi';
import { api } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Button, Card } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import PersonalHouseholdTab from './tabs/PersonalHouseholdTab';
import CashflowTab from './tabs/CashflowTab';
import BalanceSheetTab from './tabs/BalanceSheetTab';
import InsuranceTab from './tabs/InsuranceTab';
import GoalsTab from './tabs/GoalsTab';
import EstatePlanningTab from './tabs/EstatePlanningTab';

const TABS = [
  { id: 'personal', label: 'Personal' },
  { id: 'cashflow', label: 'Cashflow' },
  { id: 'balance', label: 'Balance Sheet' },
  { id: 'insurance', label: 'Insurance' },
  { id: 'goals', label: 'Goals' },
  { id: 'estate', label: 'Estate Planning' },
];

const AUTOSAVE_MS = 1500;

const KIND_TO_KEY = {
  income: 'income_sources',
  expenses: 'expenses',
  assets: 'assets',
  liabilities: 'liabilities',
  policies: 'insurance_policies',
  goals: 'financial_goals',
  dependents: 'dependents',
  estate: 'estate_checklist',
};

function leadDisplayName(lead) {
  if (!lead) return 'Lead';
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
  return name || 'Lead';
}

function apiModuleForKind(kind) {
  if (kind === 'income') return api.financialAnalysis.incomeSources;
  if (kind === 'expenses') return api.financialAnalysis.expenses;
  if (kind === 'assets') return api.financialAnalysis.assets;
  if (kind === 'liabilities') return api.financialAnalysis.liabilities;
  if (kind === 'policies') return api.financialAnalysis.insurancePolicies;
  if (kind === 'goals') return api.financialAnalysis.financialGoals;
  if (kind === 'dependents') return api.financialAnalysis.dependents;
  return null;
}

export default function FinancialAnalysisDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | unsaved | saving | saved

  const incomeRef = useRef([]);
  const expensesRef = useRef([]);
  const assetsRef = useRef([]);
  const liabilitiesRef = useRef([]);
  const policiesRef = useRef([]);
  const goalsRef = useRef([]);
  const dependentsRef = useRef([]);
  const estateRef = useRef([]);
  const parentFieldsRef = useRef({});
  const analysisIdRef = useRef(null);
  const dirtyRowsRef = useRef(new Map());
  const timersRef = useRef(new Map());
  const savedTimerRef = useRef(null);

  const syncRefsFromAnalysis = useCallback((data) => {
    incomeRef.current = data.income_sources || [];
    expensesRef.current = data.expenses || [];
    assetsRef.current = data.assets || [];
    liabilitiesRef.current = data.liabilities || [];
    policiesRef.current = data.insurance_policies || [];
    goalsRef.current = data.financial_goals || [];
    dependentsRef.current = data.dependents || [];
    estateRef.current = data.estate_checklist || [];
    parentFieldsRef.current = {
      years_to_replace: data.years_to_replace ?? 20,
      final_expenses_override: data.final_expenses_override ?? null,
      education_fund: data.education_fund ?? 0,
      legacy_wishes: data.legacy_wishes ?? '',
      date_of_birth: data.date_of_birth ?? null,
      employment_status: data.employment_status ?? null,
      occupation: data.occupation ?? '',
      marital_status: data.marital_status ?? null,
      spouse_first_name: data.spouse_first_name ?? '',
      spouse_last_name: data.spouse_last_name ?? '',
      spouse_email: data.spouse_email ?? '',
      spouse_phone: data.spouse_phone ?? '',
      spouse_date_of_birth: data.spouse_date_of_birth ?? null,
      spouse_lead_id: data.spouse_lead_id ?? null,
    };
    analysisIdRef.current = data.id;
  }, []);

  const setRowsForKind = useCallback((kind, rowsOrUpdater) => {
    setAnalysis((prev) => {
      if (!prev) return prev;
      const key = KIND_TO_KEY[kind];
      if (!key) return prev;
      const current = prev[key] || [];
      const next = typeof rowsOrUpdater === 'function' ? rowsOrUpdater(current) : rowsOrUpdater;
      const updated = { ...prev, [key]: next };
      if (kind === 'income') incomeRef.current = next;
      if (kind === 'expenses') expensesRef.current = next;
      if (kind === 'assets') assetsRef.current = next;
      if (kind === 'liabilities') liabilitiesRef.current = next;
      if (kind === 'policies') policiesRef.current = next;
      if (kind === 'goals') goalsRef.current = next;
      if (kind === 'dependents') dependentsRef.current = next;
      if (kind === 'estate') estateRef.current = next;
      return updated;
    });
  }, []);

  const markSavedBriefly = useCallback(() => {
    setSaveStatus('saved');
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => {
      setSaveStatus((s) => (s === 'saved' ? 'idle' : s));
    }, 2000);
  }, []);

  const flushRow = useCallback(async (dirtyKey) => {
    const entry = dirtyRowsRef.current.get(dirtyKey);
    if (!entry) return;
    dirtyRowsRef.current.delete(dirtyKey);
    const timer = timersRef.current.get(dirtyKey);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(dirtyKey);
    }
    await entry.save();
  }, []);

  const flushAll = useCallback(async () => {
    const keys = [...dirtyRowsRef.current.keys()];
    if (keys.length === 0) return;
    setSaveStatus('saving');
    try {
      await Promise.all(keys.map((key) => flushRow(key)));
      markSavedBriefly();
    } catch (err) {
      setSaveStatus('unsaved');
      throw err;
    }
  }, [flushRow, markSavedBriefly]);

  const scheduleDirtySave = useCallback(
    (dirtyKey, saveFn) => {
      dirtyRowsRef.current.set(dirtyKey, { save: saveFn });
      setSaveStatus('unsaved');
      const existing = timersRef.current.get(dirtyKey);
      if (existing) clearTimeout(existing);
      timersRef.current.set(
        dirtyKey,
        setTimeout(async () => {
          timersRef.current.delete(dirtyKey);
          if (!dirtyRowsRef.current.has(dirtyKey)) return;
          setSaveStatus('saving');
          try {
            await flushRow(dirtyKey);
            if (dirtyRowsRef.current.size === 0) markSavedBriefly();
            else setSaveStatus('unsaved');
          } catch (err) {
            setSaveStatus('unsaved');
            addToast({
              type: 'error',
              title: 'Autosave failed',
              message: err?.message,
            });
          }
        }, AUTOSAVE_MS),
      );
    },
    [addToast, flushRow, markSavedBriefly],
  );

  const scheduleRowSave = useCallback(
    (kind, rowId, buildPayload) => {
      const mod = apiModuleForKind(kind);
      if (!mod) return;
      scheduleDirtySave(`${kind}:${rowId}`, async () => {
        await mod.update(rowId, buildPayload());
      });
    },
    [scheduleDirtySave],
  );

  const scheduleParentSave = useCallback(
    (buildPayload) => {
      scheduleDirtySave('parent:fields', async () => {
        const analysisId = analysisIdRef.current;
        if (!analysisId) return;
        const payload = buildPayload();
        const updated = await api.financialAnalysis.update(analysisId, payload);
        setAnalysis((prev) => (prev ? { ...prev, ...updated } : prev));
        parentFieldsRef.current = {
          ...parentFieldsRef.current,
          ...payload,
        };
      });
    },
    [scheduleDirtySave],
  );

  const schedulePersonalSave = useCallback(
    (buildPayload) => {
      scheduleDirtySave('parent:personal', async () => {
        const analysisId = analysisIdRef.current;
        if (!analysisId) return;
        const payload = buildPayload();
        const updated = await api.financialAnalysis.update(analysisId, payload);
        setAnalysis((prev) => (prev ? { ...prev, ...updated } : prev));
        parentFieldsRef.current = {
          ...parentFieldsRef.current,
          ...payload,
        };
      });
    },
    [scheduleDirtySave],
  );

  const scheduleEstateItemSave = useCallback(
    (itemId, buildPayload) => {
      scheduleDirtySave(`estate:${itemId}`, async () => {
        await api.financialAnalysis.estateChecklist.updateItem(itemId, buildPayload());
      });
    },
    [scheduleDirtySave],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.financialAnalysis.getById(id);
      if (!data) {
        addToast({ type: 'error', title: 'Financial analysis not found' });
        navigate('/financial-analysis');
        return;
      }
      setAnalysis(data);
      syncRefsFromAnalysis(data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Failed to load financial analysis',
        message: err?.message,
      });
      navigate('/financial-analysis');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, addToast, syncRefsFromAnalysis]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const handleSaveNow = async () => {
    try {
      await flushAll();
      addToast({ type: 'success', title: 'Saved', message: 'Financial analysis saved.' });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Save failed',
        message: err?.message,
      });
    }
  };

  const statusLabel = useMemo(() => {
    if (saveStatus === 'saving') return 'Saving…';
    if (saveStatus === 'saved') return 'Saved';
    if (saveStatus === 'unsaved') return 'Unsaved changes';
    return '';
  }, [saveStatus]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 gap-3 text-gray-500 text-sm">
        <Spinner className="text-primary" />
        Loading financial analysis…
      </div>
    );
  }

  if (!analysis) return null;

  const leadName = leadDisplayName(analysis.lead);
  const leadId = analysis.lead_id || analysis.lead?.id;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => navigate('/financial-analysis')}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            Back to list
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Financial Analysis</h1>
            {leadId ? (
              <Link
                to={`/leads/${leadId}`}
                className="text-sm text-primary hover:underline inline-flex items-center gap-1 mt-1"
              >
                {leadName}
                <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
              </Link>
            ) : (
              <p className="text-sm text-gray-500 mt-1">{leadName}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {statusLabel && (
            <span
              className={`text-xs font-medium ${
                saveStatus === 'unsaved'
                  ? 'text-amber-600'
                  : saveStatus === 'saving'
                    ? 'text-gray-500'
                    : 'text-emerald-600'
              }`}
            >
              {statusLabel}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveNow}
            disabled={saveStatus === 'saving'}
            isLoading={saveStatus === 'saving'}
          >
            <SafeIcon icon={FiSave} className="w-4 h-4 mr-1.5" />
            Save
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/financial-analysis/${analysis.id}/report`)}
          >
            View Report
          </Button>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-1 -mb-px">
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'personal' && (
        <PersonalHouseholdTab
          analysisId={analysis.id}
          agencyId={analysis.agency_id}
          analysis={analysis}
          dependents={analysis.dependents || []}
          setDependents={(rows) => setRowsForKind('dependents', rows)}
          scheduleParentSave={schedulePersonalSave}
          scheduleRowSave={scheduleRowSave}
          setSaveStatus={setSaveStatus}
          markSavedBriefly={markSavedBriefly}
          parentFieldsRef={parentFieldsRef}
          dependentsRef={dependentsRef}
          onAnalysisPatch={(patch) =>
            setAnalysis((prev) => (prev ? { ...prev, ...patch } : prev))
          }
        />
      )}
      {activeTab === 'cashflow' && (
        <CashflowTab
          analysisId={analysis.id}
          agencyId={analysis.agency_id}
          incomeSources={analysis.income_sources || []}
          expenses={analysis.expenses || []}
          setIncomeSources={(rows) => setRowsForKind('income', rows)}
          setExpenses={(rows) => setRowsForKind('expenses', rows)}
          scheduleRowSave={scheduleRowSave}
          setSaveStatus={setSaveStatus}
          markSavedBriefly={markSavedBriefly}
          incomeRef={incomeRef}
          expensesRef={expensesRef}
        />
      )}
      {activeTab === 'balance' && (
        <BalanceSheetTab
          analysisId={analysis.id}
          agencyId={analysis.agency_id}
          assets={analysis.assets || []}
          liabilities={analysis.liabilities || []}
          setAssets={(rows) => setRowsForKind('assets', rows)}
          setLiabilities={(rows) => setRowsForKind('liabilities', rows)}
          scheduleRowSave={scheduleRowSave}
          setSaveStatus={setSaveStatus}
          markSavedBriefly={markSavedBriefly}
          assetsRef={assetsRef}
          liabilitiesRef={liabilitiesRef}
        />
      )}
      {activeTab === 'insurance' && (
        <InsuranceTab
          analysisId={analysis.id}
          agencyId={analysis.agency_id}
          analysis={analysis}
          incomeSources={analysis.income_sources || []}
          assets={analysis.assets || []}
          insurancePolicies={analysis.insurance_policies || []}
          setInsurancePolicies={(rows) => setRowsForKind('policies', rows)}
          scheduleRowSave={scheduleRowSave}
          scheduleParentSave={scheduleParentSave}
          setSaveStatus={setSaveStatus}
          markSavedBriefly={markSavedBriefly}
          policiesRef={policiesRef}
          parentFieldsRef={parentFieldsRef}
        />
      )}
      {activeTab === 'goals' && (
        <GoalsTab
          analysisId={analysis.id}
          agencyId={analysis.agency_id}
          financialGoals={analysis.financial_goals || []}
          setFinancialGoals={(rows) => setRowsForKind('goals', rows)}
          scheduleRowSave={scheduleRowSave}
          setSaveStatus={setSaveStatus}
          markSavedBriefly={markSavedBriefly}
          goalsRef={goalsRef}
        />
      )}
      {activeTab === 'estate' && (
        <EstatePlanningTab
          analysis={analysis}
          incomeSources={analysis.income_sources || []}
          expenses={analysis.expenses || []}
          assets={analysis.assets || []}
          liabilities={analysis.liabilities || []}
          insurancePolicies={analysis.insurance_policies || []}
          estateChecklist={analysis.estate_checklist || []}
          setEstateChecklist={(rows) => setRowsForKind('estate', rows)}
          scheduleEstateItemSave={scheduleEstateItemSave}
          scheduleParentSave={scheduleParentSave}
          parentFieldsRef={parentFieldsRef}
          estateRef={estateRef}
        />
      )}
    </div>
  );
}
